/** landing-modes UI: which tier gated a landing, its stack, and the conflict task it waits on. */
import { Link } from '@tanstack/react-router'
import { gateView, type StackView } from '../lib/landing-modes.js'
import type { Landing } from '../lib/types.js'

const TONE: Record<string, string> = {
  Passed: 'text-status-ok',
  'Already verified (reused)': 'text-status-ok',
  Failed: 'text-status-fail',
  Running: 'text-status-run',
}

/** The tier that ran, drillable: its commands with pass / reused / failed marks. */
export function GateEvidence({ landing }: { landing: Pick<Landing, 'gate' | 'recipe'> }) {
  const g = gateView(landing)
  if (!g) return null
  return (
    <section className="rounded-lg border border-border-subtle p-2" data-landing-gate>
      <details>
        <summary className="cursor-pointer text-[14px] font-semibold" data-gate-summary>
          {g.summary}
        </summary>
        <p className="m-0 mt-1 text-[14px] text-text-2">
          Chosen from {g.files} changed file{g.files === 1 ? '' : 's'} (strictness {g.strictness}).
        </p>
        <ol className="m-0 mt-1 grid list-none gap-1 p-0" aria-label="Gate commands">
          {g.commands.map((c) => (
            <li key={c.command} className="flex min-w-0 items-center gap-2 text-[14px]">
              <span className={`flex-none font-semibold ${TONE[c.label] ?? 'text-text-3'}`}>
                {c.label}
              </span>
              <code className="min-w-0 flex-1 truncate font-mono">{c.command}</code>
            </li>
          ))}
        </ol>
      </details>
    </section>
  )
}

/** Integration branch → children with status, each drillable to its landing. */
export function StackBlock({
  stack,
  onOpen,
}: {
  stack: StackView
  onOpen?: (landingId: string) => void
}) {
  const open = (id: string, text: string) =>
    onOpen ? (
      <button type="button" className="lk-link text-[14px]" onClick={() => onOpen(id)}>
        {text}
      </button>
    ) : (
      <span>{text}</span>
    )
  return (
    <section className="rounded-lg border border-border-subtle p-2" data-landing-stack>
      <details open>
        <summary className="cursor-pointer text-[14px] font-semibold">
          Stack: {stack.integration}
          {stack.into ? ` → ${stack.into}` : ''}
        </summary>
        <p className="m-0 mt-1 text-[14px] text-text-2">
          Children land into <code>{stack.integration}</code> first, then it lands
          {stack.into ? ` into ${stack.into}` : ' into the default branch'}.
        </p>
        <ol className="m-0 mt-1 grid list-none gap-1 p-0" aria-label="Landing order">
          {stack.children.map((c, i) => (
            <li key={c.landingId} className="flex items-center gap-2 text-[14px]">
              <span className="flex-none text-text-3">{i + 1}.</span>
              {open(c.landingId, c.branch)}
              <span className="text-text-3">{c.label}</span>
            </li>
          ))}
          <li className="flex items-center gap-2 text-[14px]" data-stack-integration>
            <span className="flex-none text-text-3">{stack.children.length + 1}.</span>
            {stack.integrationLanding
              ? open(stack.integrationLanding.landingId, stack.integration)
              : stack.integration}
            <span className="text-text-3">
              {stack.integrationLanding?.label ?? 'waits for its children'}
            </span>
          </li>
        </ol>
      </details>
    </section>
  )
}

/** "Waiting on conflict task" with a link to the task. */
export function ConflictWait({ landing }: { landing: Pick<Landing, 'waitingOn'> }) {
  const w = landing.waitingOn
  if (!w) return null
  return (
    <p className="m-0 text-[14px] text-status-wait" data-conflict-wait>
      Waiting on conflict task:{' '}
      <Link to="/t/$taskId" params={{ taskId: w.taskId }} className="text-accent-text">
        {w.title}
      </Link>
    </p>
  )
}

export function TargetLine({ landing }: { landing: Pick<Landing, 'targetBranch'> }) {
  if (!landing.targetBranch) return null
  return (
    <p className="m-0 text-[14px] text-text-2" data-landing-target>
      Target branch <code>{landing.targetBranch}</code>
    </p>
  )
}
